import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { InputWithDropdown } from '@/components/ui/input-with-dropdown';
import { useDebounce } from '@/hooks/useDebounce';

interface TotalSpendFilterProps {
  onChange: (lowerLimit: number | null, upperLimit: number | null) => void;
  lowerLimit: number | null;
  upperLimit: number | null;
}

const TotalSpendFilter: React.FC<TotalSpendFilterProps> = ({
  onChange,
  lowerLimit = 0,
  upperLimit = null
}) => {
  const [localLowerLimit, setLocalLowerLimit] = useState<number>(lowerLimit || 0);
  const [localUpperLimit, setLocalUpperLimit] = useState<number | null>(upperLimit);
  const [lowerInputValue, setLowerInputValue] = useState<string>((lowerLimit || 0).toString());
  const [upperInputValue, setUpperInputValue] = useState<string>(upperLimit ? upperLimit.toString() : '');
  
  // Common spend amounts for quick selection
  const commonAmounts = [0, 50, 100, 200, 300, 500, 750, 1000, 1500, 2000, 3000, 5000, 10000];
  
  // Debounce the values to prevent too many API calls
  const debouncedLowerLimit = useDebounce(localLowerLimit, 500);
  const debouncedUpperLimit = useDebounce(localUpperLimit, 500);

  // Update local state when props change
  useEffect(() => {
    setLocalLowerLimit(lowerLimit || 0);
    setLocalUpperLimit(upperLimit);
    setLowerInputValue((lowerLimit || 0).toString());
    setUpperInputValue(upperLimit ? upperLimit.toString() : '');
  }, [lowerLimit, upperLimit]);
  
  // Call onChange when debounced values change
  useEffect(() => {
    onChange(debouncedLowerLimit, debouncedUpperLimit);
  }, [debouncedLowerLimit, debouncedUpperLimit, onChange]);

  // Generate options for dropdowns
  const lowerOptions = commonAmounts.map(amount => ({
    value: amount.toString(),
    label: `$${amount.toLocaleString()}`
  }));
  
  const upperOptions = [
    { value: 'any', label: 'Any' },
    ...commonAmounts
      .filter(amount => amount > localLowerLimit)
      .map(amount => ({
        value: amount.toString(),
        label: `$${amount.toLocaleString()}`
      }))
  ];

  const handleLowerInputChange = (value: string) => {
    setLowerInputValue(value);
    
    if (value === '') {
      setLocalLowerLimit(0);
      setLowerInputValue('0');
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 0) {
        setLocalLowerLimit(numValue);
        
        // If upper limit is set and lower than new lower limit, clear it
        if (localUpperLimit !== null && localUpperLimit <= numValue) {
          setLocalUpperLimit(null);
          setUpperInputValue('');
        }
      }
    }
  };

  const handleUpperInputChange = (value: string) => {
    setUpperInputValue(value);
    
    if (value === '' || value.toLowerCase() === 'any') {
      setLocalUpperLimit(null);
      setUpperInputValue('');
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue > localLowerLimit) {
        setLocalUpperLimit(numValue);
      }
    }
  };
  
  const handleLowerInputBlur = () => {
    if (lowerInputValue === '' || isNaN(parseInt(lowerInputValue))) {
      setLowerInputValue('0');
      setLocalLowerLimit(0);
    }
  };
  
  const handleUpperInputBlur = () => {
    if (upperInputValue !== '' && !isNaN(parseInt(upperInputValue))) {
      const value = parseInt(upperInputValue);
      if (value <= localLowerLimit) {
        setUpperInputValue('');
        setLocalUpperLimit(null);
      }
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-base">Total Spend</Label>
      
      <div className="flex flex-col md:flex-row gap-4 w-full">
        <div className="space-y-2 flex-1">
          <Label htmlFor="min-total-spend">Minimum Spend</Label>
          <InputWithDropdown
            id="min-total-spend"
            type="number"
            min={0}
            value={lowerInputValue}
            onChange={handleLowerInputChange}
            onBlur={handleLowerInputBlur}
            options={lowerOptions}
            placeholder="Enter or select"
            prefix={<span className="text-sm text-gray-500">$</span>}
          />
        </div>
        
        <div className="space-y-2 flex-1">
          <Label htmlFor="max-total-spend">Maximum Spend</Label>
          <InputWithDropdown
            id="max-total-spend"
            type="text"
            value={upperInputValue}
            onChange={handleUpperInputChange}
            onBlur={handleUpperInputBlur}
            options={upperOptions}
            placeholder="Any"
            prefix={<span className="text-sm text-gray-500">$</span>}
          />
        </div>
      </div>
    </div>
  );
};

export default TotalSpendFilter;